import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CloudUpload, History, ListAlt, Receipt, Search } from "@material-ui/icons"
import { Autocomplete } from "@material-ui/lab"
import React, { useEffect, useMemo, useRef, useState } from "react"
import type { NfeResumo, NfseResumo } from "../../services/emissoesParaMeuCNPJ"
import {
    baixarArquivoNfe,
    baixarArquivoNfse,
    buscarEmissoes,
} from "../../services/emissoesParaMeuCNPJ"
import { buscarInsumos } from "../../services/insumos"
import type {
    NotaCompraImportada,
    ResultadoImportacao,
    SugestaoVinculo,
} from "../../services/notasCompra"
import {
    cancelarNotaCompra,
    importarNotaCompra,
    listarNotasCompra,
    sugerirVinculos,
    verificarNotaCompra,
} from "../../services/notasCompra"
import useAppStore from "../../stores/AppStore"
import useLoadingStore from "../../stores/LoadingStore"
import type { NotaNfe } from "../../utils/nfeXml"
import { lerArquivoXml, lerNfeXml } from "../../utils/nfeXml"
import { useSnackbar } from "../../utils/notificacoes"
import Cabecalho from "../Cabecalho"
import { Loading } from "../RegistradorDePedidos"

type Insumo = {
    id: number
    nome: string
    unidade_medida: string
    controlar_estoque: boolean
    valor_unitario: string | number | null
    estoque?: { quantidade: string | number | null } | null
}

type LinhaItem = {
    numeroItem: number
    codigoProduto: string
    ean: string | null
    descricao: string
    unidade: string
    quantidade: number
    valorUnitario: number
    valorTotal: number
    insumoId: number | null
    fator: string
    origem: SugestaoVinculo["origem"]
}

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
const fmtQtd = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 3 })
const fmtCusto = (v: number) =>
    v.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    })
const fmtCnpj = (v: string | null | undefined) => {
    const d = (v || "").replace(/\D/g, "")
    if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")
    return v || "-"
}
const fmtData = (v: string | null | undefined) => {
    if (!v) return "-"
    const d = new Date(v)
    return isNaN(d.getTime()) ? v : d.toLocaleDateString("pt-BR")
}
const isoDia = (d: Date) => d.toISOString().slice(0, 10)
const numFator = (v: string) => {
    const n = parseFloat(String(v).replace(",", "."))
    return Number.isFinite(n) ? n : NaN
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "12px",
            paddingRight: "12px",
            paddingTop: "70px",
        },
    },
    content: { margin: "auto", maxWidth: 1200 },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    card: {
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
    },
    cardHeaderTitle: { fontWeight: 600, color: "#2c3e50", flex: 1 },
    cardContent: { padding: theme.spacing(2.5) },
    iconHeader: { color: "#4caf50" },
    linhaAcoes: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        flexWrap: "wrap",
    },
    botaoPrincipal: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        padding: theme.spacing(1, 3),
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    resumoNota: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    rotulo: { color: "#7f8c8d", fontSize: "0.78rem" },
    valor: { fontWeight: 600, color: "#2c3e50", wordBreak: "break-all" },
    hint: { color: "#7f8c8d", fontSize: "0.85rem" },
    celulaItem: { minWidth: 220, maxWidth: 320 },
    celulaInsumo: { minWidth: 240 },
    celulaFator: { width: 120 },
    linhaIgnorada: { opacity: 0.55 },
    aviso: {
        backgroundColor: "#fff3cd",
        border: "1px solid #ffe69c",
        borderRadius: 8,
        padding: theme.spacing(1.5),
        color: "#8a5b00",
        marginBottom: theme.spacing(2),
    },
}))

export default function ImportarNotasCompra() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)
    const configs = useAppStore(s => s.configuracoes) as any
    const idEstabelecimento: number | undefined = configs?.user?.parametros?.estabelecimento?.id
    const cnpjLoja: string = String(configs?.user?.parametros?.estabelecimento?.cnpj || "").replace(
        /\D/g,
        ""
    )
    const autorizacao = localStorage.getItem("chaveSistema") || ""

    const inputArquivo = useRef<HTMLInputElement>(null)
    const [insumos, setInsumos] = useState<Insumo[]>([])
    const [historico, setHistorico] = useState<NotaCompraImportada[]>([])

    // Notas destinadas ao CNPJ (PlugNotas)
    const hoje = new Date()
    const [dataInicial, setDataInicial] = useState(isoDia(new Date(hoje.getTime() - 30 * 864e5)))
    const [dataFinal, setDataFinal] = useState(isoDia(hoje))
    const [destinadas, setDestinadas] = useState<NfeResumo[] | null>(null)
    const [servicos, setServicos] = useState<NfseResumo[]>([])

    // Nota em conferência
    const [nota, setNota] = useState<NotaNfe | null>(null)
    const [linhas, setLinhas] = useState<LinhaItem[]>([])
    const [jaImportada, setJaImportada] = useState<NotaCompraImportada | null>(null)
    const [confirmando, setConfirmando] = useState(false)
    const [resultado, setResultado] = useState<ResultadoImportacao | null>(null)

    // Nota importada cujo cancelamento está sendo perguntado
    const [cancelando, setCancelando] = useState<NotaCompraImportada | null>(null)

    const alerta = (msg: string, variant: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant })

    const carregarHistorico = async () => {
        if (!idEstabelecimento) return
        const r = await listarNotasCompra(idEstabelecimento, autorizacao)
        if (!("retornoErro" in r)) setHistorico(r)
    }

    useEffect(() => {
        if (!idEstabelecimento) return
        setLoading(true)
        Promise.all([
            buscarInsumos(idEstabelecimento, autorizacao).then((r: any) => {
                if (r?.retornoErro) alerta(r.mensagem || "Erro ao carregar insumos", "error")
                else if (Array.isArray(r)) setInsumos(r)
            }),
            carregarHistorico(),
        ]).finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimento])

    const insumoPorId = useMemo(() => {
        const m = new Map<number, Insumo>()
        insumos.forEach(i => m.set(Number(i.id), i))
        return m
    }, [insumos])

    // ─── Carregar nota ────────────────────────────────────────────────────────

    const abrirNota = async (conteudo: string) => {
        if (!idEstabelecimento) return
        let lida: NotaNfe
        try {
            lida = lerNfeXml(conteudo)
        } catch (e) {
            alerta((e as Error).message, "error")
            return
        }

        setLoading(true)
        try {
            const [verif, sug] = await Promise.all([
                verificarNotaCompra(idEstabelecimento, lida.chave, autorizacao),
                sugerirVinculos(
                    idEstabelecimento,
                    lida.fornecedorCnpj,
                    lida.itens.map(i => ({ codigoProduto: i.codigoProduto, ean: i.ean })),
                    autorizacao
                ),
            ])
            if ("retornoErro" in verif) {
                alerta(verif.mensagem, "error")
                return
            }
            const sugestoes = "retornoErro" in sug ? [] : sug.sugestoes
            if ("retornoErro" in sug)
                alerta("Não foi possível buscar os vínculos: " + sug.mensagem, "warning")

            setNota(lida)
            setJaImportada(verif.importada ? verif.nota : null)
            setResultado(null)
            setLinhas(
                lida.itens.map(item => {
                    const s = sugestoes.find(x => x.codigoProduto === item.codigoProduto)
                    return {
                        ...item,
                        insumoId: s?.insumoId ?? null,
                        fator: String(s?.fatorConversao ?? 1).replace(".", ","),
                        origem: s?.origem ?? null,
                    }
                })
            )
            if (cnpjLoja && lida.destinatarioCnpj && lida.destinatarioCnpj !== cnpjLoja) {
                alerta(
                    "Atenção: o destinatário desta nota não é o CNPJ deste estabelecimento.",
                    "warning"
                )
            }
        } finally {
            setLoading(false)
        }
    }

    const handleArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0]
        e.target.value = ""
        if (!arquivo) return
        abrirNota(await lerArquivoXml(arquivo))
    }

    const buscarDestinadas = async () => {
        setLoading(true)
        try {
            const r = await buscarEmissoes(autorizacao, dataInicial, dataFinal)
            if ("retornoErro" in r) {
                alerta(r.mensagem || "Erro ao buscar notas", "error")
                return
            }
            if (r.mensagemMercadoria) alerta(r.mensagemMercadoria, "info")
            if (r.mensagemServico) alerta(r.mensagemServico, "info")
            setDestinadas(r.notasMercadoria || [])
            setServicos(r.notasServico || [])
        } finally {
            setLoading(false)
        }
    }

    const baixarPdfServico = async (n: NfseResumo) => {
        setLoading(true)
        try {
            const blob = await baixarArquivoNfse(autorizacao, n.id)
            if ("retornoErro" in blob) {
                alerta(blob.mensagem || "Erro ao baixar o PDF", "error")
                return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `NFSE_${n.numeroNfse || n.id}.pdf`
            a.click()
            URL.revokeObjectURL(url)
        } finally {
            setLoading(false)
        }
    }

    const abrirDestinada = async (n: NfeResumo) => {
        if (!n.xml) {
            alerta("Esta nota não tem XML disponível na PlugNotas.", "error")
            return
        }
        setLoading(true)
        try {
            const blob = await baixarArquivoNfe(autorizacao, n.xml, "xml")
            if ("retornoErro" in blob) {
                alerta(blob.mensagem || "Erro ao baixar o XML", "error")
                return
            }
            const conteudo = await lerArquivoXml(blob)
            setLoading(false)
            await abrirNota(conteudo)
        } finally {
            setLoading(false)
        }
    }

    // ─── Conferência ─────────────────────────────────────────────────────────

    const atualizarLinha = (idx: number, parcial: Partial<LinhaItem>) =>
        setLinhas(ls => ls.map((l, i) => (i === idx ? { ...l, ...parcial } : l)))

    const linhasLancadas = linhas.filter(l => l.insumoId != null)
    const fatorInvalido = linhasLancadas.some(l => !(numFator(l.fator) > 0))

    const confirmarImportacao = async () => {
        if (!nota || !idEstabelecimento) return
        setConfirmando(false)
        setLoading(true)
        try {
            const r = await importarNotaCompra(
                idEstabelecimento,
                {
                    chave: nota.chave,
                    numero: nota.numero,
                    serie: nota.serie,
                    dataEmissao: nota.dataEmissao,
                    fornecedorCnpj: nota.fornecedorCnpj,
                    fornecedorNome: nota.fornecedorNome,
                    valorTotal: nota.valorTotal,
                    itens: linhas.map(l => ({
                        codigoProduto: l.codigoProduto,
                        ean: l.ean,
                        descricao: l.descricao,
                        unidade: l.unidade,
                        quantidade: l.quantidade,
                        valorUnitario: l.valorUnitario,
                        insumoId: l.insumoId,
                        fatorConversao: l.insumoId != null ? numFator(l.fator) : 1,
                    })),
                },
                autorizacao
            )
            if ("retornoErro" in r) {
                alerta(r.mensagem || "Erro ao importar a nota", "error")
                return
            }
            setResultado(r)
            alerta(`Entrada registrada: ${r.itensLancados} item(ns) no estoque.`, "success")
            // Recarrega insumos (saldo/custo novos) e o histórico.
            const ins: any = await buscarInsumos(idEstabelecimento, autorizacao)
            if (Array.isArray(ins)) setInsumos(ins)
            await carregarHistorico()
        } finally {
            setLoading(false)
        }
    }

    const confirmarCancelamento = async (reverterEstoque: boolean) => {
        if (!cancelando || !idEstabelecimento) return
        const notaCancelar = cancelando
        setCancelando(null)
        setLoading(true)
        try {
            const r = await cancelarNotaCompra(
                idEstabelecimento,
                notaCancelar.id,
                reverterEstoque,
                autorizacao
            )
            if ("retornoErro" in r) {
                alerta(r.mensagem || "Erro ao cancelar a importação", "error")
                return
            }
            alerta(r.mensagem, "success")
            if (r.insumosNegativos.length > 0) {
                alerta(
                    "Estoque negativo após a reversão: " +
                        r.insumosNegativos.map(i => i.insumo).join(", "),
                    "warning"
                )
            }
            if (reverterEstoque) {
                const ins: any = await buscarInsumos(idEstabelecimento, autorizacao)
                if (Array.isArray(ins)) setInsumos(ins)
            }
            await carregarHistorico()
        } finally {
            setLoading(false)
        }
    }

    const fecharNota = () => {
        setNota(null)
        setLinhas([])
        setJaImportada(null)
        setResultado(null)
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    const renderSelecao = () => (
        <>
            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <CloudUpload className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Enviar XML da nota
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <Box className={classes.linhaAcoes}>
                        <input
                            ref={inputArquivo}
                            type="file"
                            accept=".xml,text/xml,application/xml"
                            style={{ display: "none" }}
                            onChange={handleArquivo}
                        />
                        <Button
                            variant="contained"
                            className={classes.botaoPrincipal}
                            startIcon={<CloudUpload />}
                            onClick={() => inputArquivo.current?.click()}
                        >
                            Selecionar arquivo XML
                        </Button>
                        <Typography className={classes.hint}>
                            XML da NF-e (modelo 55) enviado pelo fornecedor.
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Receipt className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Notas emitidas para o meu CNPJ
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <Box className={classes.linhaAcoes} mb={2}>
                        <TextField
                            type="date"
                            label="De"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={dataInicial}
                            onChange={e => setDataInicial(e.target.value)}
                        />
                        <TextField
                            type="date"
                            label="Até"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            value={dataFinal}
                            onChange={e => setDataFinal(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Search />}
                            onClick={buscarDestinadas}
                        >
                            Buscar notas
                        </Button>
                    </Box>
                    {destinadas === null ? (
                        <Typography className={classes.hint}>
                            Busca as NF-e e NFS-e emitidas contra o CNPJ da loja (via PlugNotas) no período.
                        </Typography>
                    ) : destinadas.length === 0 && servicos.length === 0 ? (
                        <Typography className={classes.hint}>
                            Nenhuma nota encontrada no período.
                        </Typography>
                    ) : destinadas.length === 0 ? (
                        <Typography className={classes.hint}>
                            Nenhuma NF-e de mercadoria no período.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Emissão</TableCell>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell>Número</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {destinadas.map(n => {
                                        const importada = historico.some(h => h.chave === n.chave)
                                        return (
                                            <TableRow key={n.id || n.chave} hover>
                                                <TableCell>{fmtData(n.dataEmissao)}</TableCell>
                                                <TableCell>{n.emitente || "-"}</TableCell>
                                                <TableCell>
                                                    {n.numero}
                                                    {n.serie ? `/${n.serie}` : ""}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {n.valorNfe != null
                                                        ? brl.format(n.valorNfe)
                                                        : "-"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {importada ? (
                                                        <Chip size="small" label="Importada" />
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            disabled={!n.xml}
                                                            onClick={() => abrirDestinada(n)}
                                                        >
                                                            Importar
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {servicos.length > 0 && (
                        <Box mt={3}>
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                Notas de serviço (NFS-e)
                            </Typography>
                            <Typography className={classes.hint}>
                                Notas de serviço não têm itens para lançar no estoque.
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Emissão</TableCell>
                                            <TableCell>Prestador</TableCell>
                                            <TableCell>Número</TableCell>
                                            <TableCell align="right">Valor</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {servicos.map(n => (
                                            <TableRow key={n.id} hover>
                                                <TableCell>{n.emissao || "-"}</TableCell>
                                                <TableCell>
                                                    {n.prestadorNomeFantasia ||
                                                        n.prestadorRazaoSocial ||
                                                        fmtCnpj(n.prestador)}
                                                    {(n.prestadorNomeFantasia ||
                                                        n.prestadorRazaoSocial) && (
                                                        <Typography className={classes.hint}>
                                                            {fmtCnpj(n.prestador)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {n.numeroNfse || "-"}
                                                    {n.serie ? `/${n.serie}` : ""}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {n.valorServico != null
                                                        ? brl.format(n.valorServico)
                                                        : "-"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => baixarPdfServico(n)}
                                                    >
                                                        PDF
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <History className={classes.iconHeader} />
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        Notas importadas
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    {historico.length === 0 ? (
                        <Typography className={classes.hint}>
                            Nenhuma nota importada ainda.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Importada em</TableCell>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell>Número</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                        <TableCell align="right">Itens lançados</TableCell>
                                        <TableCell>Usuário</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historico.map(h => (
                                        <TableRow key={h.id}>
                                            <TableCell>
                                                {new Date(h.created_at).toLocaleString("pt-BR")}
                                            </TableCell>
                                            <TableCell>
                                                {h.fornecedor_nome || "-"}
                                                <Typography className={classes.hint}>
                                                    {fmtCnpj(h.fornecedor_cnpj)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {h.numero || "-"}
                                                {h.serie ? `/${h.serie}` : ""}
                                            </TableCell>
                                            <TableCell align="right">
                                                {h.valor_total != null
                                                    ? brl.format(Number(h.valor_total))
                                                    : "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {h.itens_lancados ?? 0}/{h.quantidade_itens ?? 0}
                                            </TableCell>
                                            <TableCell>{h.usuario || "-"}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    color="secondary"
                                                    onClick={() => setCancelando(h)}
                                                >
                                                    Cancelar importação
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Paper>
        </>
    )

    const renderConferencia = (n: NotaNfe) => (
        <Paper className={classes.card}>
            <Box className={classes.cardHeader}>
                <ListAlt className={classes.iconHeader} />
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    Conferir itens da nota
                </Typography>
                <Button onClick={fecharNota}>Voltar</Button>
            </Box>
            <Box className={classes.cardContent}>
                <Box className={classes.resumoNota}>
                    <Box>
                        <Typography className={classes.rotulo}>Fornecedor</Typography>
                        <Typography className={classes.valor}>{n.fornecedorNome}</Typography>
                        <Typography className={classes.hint}>
                            {fmtCnpj(n.fornecedorCnpj)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.rotulo}>Número / série</Typography>
                        <Typography className={classes.valor}>
                            {n.numero}/{n.serie}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.rotulo}>Emissão</Typography>
                        <Typography className={classes.valor}>{fmtData(n.dataEmissao)}</Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.rotulo}>Valor total</Typography>
                        <Typography className={classes.valor}>
                            {brl.format(n.valorTotal)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.rotulo}>Chave de acesso</Typography>
                        <Typography className={classes.valor} style={{ fontSize: "0.78rem" }}>
                            {n.chave}
                        </Typography>
                    </Box>
                </Box>

                {jaImportada && !resultado && (
                    <Box className={classes.aviso}>
                        Esta nota já foi importada em{" "}
                        {new Date(jaImportada.created_at).toLocaleString("pt-BR")}
                        {jaImportada.usuario ? ` por ${jaImportada.usuario}` : ""}. Não é possível
                        lançá-la de novo.
                    </Box>
                )}

                {resultado ? (
                    <>
                        <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                            Entrada registrada: {resultado.itensLancados} item(ns) lançados
                            {resultado.itensIgnorados
                                ? `, ${resultado.itensIgnorados} ignorado(s)`
                                : ""}
                            .
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Insumo</TableCell>
                                        <TableCell align="right">Entrada</TableCell>
                                        <TableCell align="right">Saldo anterior</TableCell>
                                        <TableCell align="right">Saldo atual</TableCell>
                                        <TableCell align="right">Custo unitário</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resultado.lancamentos.map((l, i) => {
                                        const un = insumoPorId.get(l.insumoId)?.unidade_medida ?? ""
                                        return (
                                            <TableRow key={`${l.insumoId}-${i}`}>
                                                <TableCell>{l.insumo}</TableCell>
                                                <TableCell align="right">
                                                    {fmtQtd(l.quantidadeEntrada)} {un}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtQtd(l.saldoAnterior)} {un}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtQtd(l.saldoAtual)} {un}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtCusto(l.custoUnitario)}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box mt={2} display="flex" justifyContent="center">
                            <Button
                                variant="contained"
                                className={classes.botaoPrincipal}
                                onClick={fecharNota}
                            >
                                Importar outra nota
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography className={classes.hint} style={{ marginBottom: 12 }}>
                            Vincule cada produto da nota a um insumo e informe o fator de conversão:
                            quantas unidades do insumo cada unidade da nota representa (ex.: 1 CX =
                            12 U). O vínculo fica salvo e é aplicado sozinho nas próximas notas
                            deste fornecedor. Itens sem insumo não entram no estoque.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Produto na nota</TableCell>
                                        <TableCell align="right">Qtd. nota</TableCell>
                                        <TableCell align="right">Vl. unit.</TableCell>
                                        <TableCell>Insumo</TableCell>
                                        <TableCell>Fator</TableCell>
                                        <TableCell align="right">Entra no estoque</TableCell>
                                        <TableCell align="right">Custo / un. insumo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {linhas.map((l, idx) => {
                                        const insumo =
                                            l.insumoId != null
                                                ? insumoPorId.get(l.insumoId)
                                                : undefined
                                        const fator = numFator(l.fator)
                                        const fatorOk = fator > 0
                                        const un = insumo?.unidade_medida ?? ""
                                        return (
                                            <TableRow
                                                key={`${l.numeroItem}-${l.codigoProduto}`}
                                                className={
                                                    l.insumoId == null
                                                        ? classes.linhaIgnorada
                                                        : undefined
                                                }
                                            >
                                                <TableCell className={classes.celulaItem}>
                                                    <Typography
                                                        style={{
                                                            fontSize: "0.88rem",
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {l.descricao}
                                                    </Typography>
                                                    <Typography className={classes.hint}>
                                                        Cód. {l.codigoProduto}
                                                        {l.ean ? ` · EAN ${l.ean}` : ""}
                                                    </Typography>
                                                    {l.origem === "VINCULO" && (
                                                        <Chip
                                                            size="small"
                                                            label="Vínculo salvo"
                                                            style={{ marginTop: 4 }}
                                                        />
                                                    )}
                                                    {l.origem === "EAN" && (
                                                        <Tooltip title="Sugerido pelo código de barras, vinculado antes em outra nota">
                                                            <Chip
                                                                size="small"
                                                                label="Sugerido pelo EAN"
                                                                style={{ marginTop: 4 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {fmtQtd(l.quantidade)} {l.unidade}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtCusto(l.valorUnitario)}
                                                </TableCell>
                                                <TableCell className={classes.celulaInsumo}>
                                                    <Autocomplete
                                                        options={insumos}
                                                        value={insumo ?? null}
                                                        getOptionLabel={(o: Insumo) =>
                                                            `${o.nome} (${o.unidade_medida})`
                                                        }
                                                        getOptionSelected={(a: Insumo, b: Insumo) =>
                                                            a.id === b.id
                                                        }
                                                        onChange={(_e, novo: Insumo | null) =>
                                                            atualizarLinha(idx, {
                                                                insumoId: novo
                                                                    ? Number(novo.id)
                                                                    : null,
                                                                origem: null,
                                                            })
                                                        }
                                                        disabled={!!jaImportada}
                                                        noOptionsText="Nenhum insumo encontrado"
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                variant="outlined"
                                                                placeholder="Não lançar"
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell className={classes.celulaFator}>
                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        value={l.fator}
                                                        onChange={e =>
                                                            atualizarLinha(idx, {
                                                                fator: e.target.value,
                                                            })
                                                        }
                                                        error={l.insumoId != null && !fatorOk}
                                                        disabled={
                                                            l.insumoId == null || !!jaImportada
                                                        }
                                                        helperText={
                                                            insumo
                                                                ? `1 ${l.unidade || "un"} = ${
                                                                      l.fator || "?"
                                                                  } ${un}`
                                                                : " "
                                                        }
                                                        inputProps={{ inputMode: "decimal" }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {insumo && fatorOk
                                                        ? `${fmtQtd(l.quantidade * fator)} ${un}`
                                                        : "-"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {insumo && fatorOk
                                                        ? fmtCusto(l.valorUnitario / fator)
                                                        : "-"}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            mt={3}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ gap: 16 }}
                        >
                            <Typography className={classes.hint}>
                                {linhasLancadas.length} de {linhas.length} itens serão lançados
                            </Typography>
                            <Button
                                variant="contained"
                                className={classes.botaoPrincipal}
                                disabled={
                                    !!jaImportada || linhasLancadas.length === 0 || fatorInvalido
                                }
                                onClick={() => setConfirmando(true)}
                            >
                                Dar entrada no estoque
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Paper>
    )

    return (
        <div>
            <Cabecalho pagina="Importar Notas de Compra" />
            <Loading />
            <div className={classes.container}>
                <div className={classes.content}>
                    <Box mb={3}>
                        <Typography variant="h4" className={classes.titleText}>
                            Importar Notas de Compra
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Dê entrada no estoque de insumos a partir do XML da NF-e de compra
                        </Typography>
                    </Box>

                    {!idEstabelecimento ? (
                        <Typography color="error">Estabelecimento não identificado.</Typography>
                    ) : nota ? (
                        renderConferencia(nota)
                    ) : (
                        renderSelecao()
                    )}
                </div>
            </div>

            <Dialog open={confirmando} onClose={() => setConfirmando(false)}>
                <DialogTitle>Confirmar entrada no estoque</DialogTitle>
                <DialogContent>
                    <Typography>
                        Serão lançados {linhasLancadas.length} item(ns) da nota {nota?.numero} de{" "}
                        {nota?.fornecedorNome}. O saldo dos insumos será somado, o custo unitário
                        passa a ser o desta nota e a nota não poderá ser importada novamente.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmando(false)}>Cancelar</Button>
                    <Button color="primary" variant="contained" onClick={confirmarImportacao}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!cancelando} onClose={() => setCancelando(null)}>
                <DialogTitle>Cancelar importação da nota</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Nota {cancelando?.numero || "-"}
                        {cancelando?.serie ? `/${cancelando.serie}` : ""} de{" "}
                        {cancelando?.fornecedor_nome || "-"}, com {cancelando?.itens_lancados ?? 0}{" "}
                        item(ns) lançado(s) no estoque.
                    </Typography>
                    <Typography gutterBottom>
                        <strong>O estoque deve ser revertido?</strong>
                    </Typography>
                    <Typography className={classes.hint}>
                        Revertendo, as quantidades que esta nota lançou saem do estoque (o saldo pode
                        ficar negativo se parte já foi consumida). Mantendo, o estoque fica como está.
                        Nos dois casos a nota sai da lista e pode ser importada de novo.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelando(null)}>Voltar</Button>
                    <Button onClick={() => confirmarCancelamento(false)}>Não, manter estoque</Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => confirmarCancelamento(true)}
                    >
                        Sim, reverter estoque
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
